import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCurrentAppUserId } from '@/hooks/useCurrentAppUser';

export type DashboardType = 'sdr' | 'consultant';

export interface DashboardVisibility {
  id: string;
  dashboard_type: DashboardType;
  user_id: string;
  is_visible: boolean;
  added_by: string | null;
  added_at: string;
  updated_at: string;
  user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    display_name: string | null;
    email: string | null;
    active: boolean;
  };
}

export const useDashboardVisibility = (dashboardType?: DashboardType) => {
  const queryClient = useQueryClient();
  const currentAppUserId = useCurrentAppUserId();

  const { data: visibilityList, isLoading } = useQuery({
    queryKey: ['dashboard_visibility', dashboardType],
    queryFn: async () => {
      let query = supabase
        .from('dashboard_visibility')
        .select(`
          *,
          user:app_users!dashboard_visibility_user_id_fkey(
            id,
            first_name,
            last_name,
            display_name,
            email,
            active
          )
        `)
        .order('added_at', { ascending: false });

      if (dashboardType) {
        query = query.eq('dashboard_type', dashboardType);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as DashboardVisibility[];
    },
  });

  const addUserMutation = useMutation({
    mutationFn: async ({ userId, dashboardType }: { userId: string; dashboardType: DashboardType }) => {
      const { error } = await supabase
        .from('dashboard_visibility')
        .insert({
          dashboard_type: dashboardType,
          user_id: userId,
          is_visible: true,
          added_by: currentAppUserId,
        });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard_visibility'] });
      toast.success('Usuário adicionado ao dashboard com sucesso');
    },
    onError: (error: any) => {
      if (error.code === '23505') {
        toast.error('Usuário já está neste dashboard');
      } else {
        toast.error('Erro ao adicionar usuário ao dashboard');
      }
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: async ({ userId, dashboardType }: { userId: string; dashboardType: DashboardType }) => {
      const { error } = await supabase
        .from('dashboard_visibility')
        .delete()
        .eq('user_id', userId)
        .eq('dashboard_type', dashboardType);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard_visibility'] });
      toast.success('Usuário removido do dashboard');
    },
    onError: () => {
      toast.error('Erro ao remover usuário do dashboard');
    },
  });

  const toggleVisibilityMutation = useMutation({
    mutationFn: async ({ 
      userId, 
      dashboardType, 
      isVisible 
    }: { 
      userId: string; 
      dashboardType: DashboardType; 
      isVisible: boolean 
    }) => {
      const { error } = await supabase
        .from('dashboard_visibility')
        .update({ is_visible: isVisible })
        .eq('user_id', userId)
        .eq('dashboard_type', dashboardType);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard_visibility'] });
      toast.success('Visibilidade atualizada');
    },
    onError: () => {
      toast.error('Erro ao atualizar visibilidade');
    },
  });

  return {
    visibilityList,
    isLoading,
    addUser: addUserMutation.mutate,
    removeUser: removeUserMutation.mutate,
    toggleVisibility: toggleVisibilityMutation.mutate,
    isAdding: addUserMutation.isPending,
    isRemoving: removeUserMutation.isPending,
    isToggling: toggleVisibilityMutation.isPending,
  };
};